import { useEffect } from 'react';
import {
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
    ColumnDef,
    PaginationState,
    SortingState,
} from '@tanstack/react-table';
import { Box, CircularProgress } from '@mui/material';
import { useFilteredDataTableState } from '@/hooks';
import { TABLE_PAGINATION } from '@/constants';
import TableBase from './TableBase';
import ActiveFilterChips from './ActiveFilterChips';
import TablePagination from './TablePagination';
import type { ColumnFilterWithDisplayLabel, FilterConfig } from './TableHeader';
import useUrlPagination from './useUrlPagination';

export interface FilterableDataTableData<TData> {
    data: TData[];
    total: number;
    totalFiltered: number;
    limit: number;
    offset: number;
}

export interface FilterOption {
    value: string;
    label: string;
}

export interface FilterableDataTableProps<TData extends object> {
    data: FilterableDataTableData<TData>;
    isLoading?: boolean;
    columns: ColumnDef<TData>[];
    filters: FilterConfig[];
    initialFilters?: ColumnFilterWithDisplayLabel[];
    onPaginationChange?: (pagination: PaginationState) => void;
    onSortingChange?: (sorting: SortingState) => void;
    onFiltersChange?: (filters: ColumnFilterWithDisplayLabel[]) => void;
    onRowClick?: (row: TData) => void;
    noDataMessage?: string;
    defaultRowsPerPage?: number;
    rowsPerPageOptions?: number[];
    persistFilters?: boolean;
    storageKey?: string;
    meta?: Record<string, unknown>;
}

/**
 * FilterableDataTable displays a table of data with column filters, sorting, and pagination.
 * Supports syncing pagination state with URL parameters.
 *
 * @typedef {object} FilterableDataTableProps
 * @template TData
 * @property {FilterableDataTableData<TData>} data – Table data
 * @property {boolean} isLoading – Data loading flag
 * @property {ColumnDef<TData>[]} columns – Table columns
 * @property {FilterConfig[]} filters – Filters for the table headers
 * @property {ColumnFiltersState} initialFilters – Initially selected column filters
 * @property {function(PaginationState): void} onPaginationChange – Function to call when the pagination changes
 * @property {function(SortingState): void} onSortingChange – Function to call when the sorting changes
 * @property {function(ColumnFiltersState): void} onFiltersChange – Function to call when the filters change
 * @property {function(TData): void} onRowClick – Function to call when a row is clicked
 * @property {string} noDataMessage – Message to display when there is no data
 * @property {number} defaultRowsPerPage – Default number of rows per page
 * @property {number[]} rowsPerPageOptions – Options for the number of rows per page
 * @property {boolean} persistFilters – Whether to persist filters to localStorage
 * @property {string} storageKey – Key for localStorage persistence
 *
 * @param {FilterableDataTableProps<TData>} props
 * @returns {JSX.Element}
 */
const FilterableDataTable = <TData extends object>({
    data,
    isLoading = false,
    columns,
    filters,
    initialFilters = [],
    onPaginationChange,
    onSortingChange,
    onFiltersChange,
    onRowClick,
    noDataMessage = 'No data found',
    defaultRowsPerPage = TABLE_PAGINATION.DEFAULT_ROWS_PER_PAGE,
    rowsPerPageOptions = TABLE_PAGINATION.ROWS_PER_PAGE_OPTIONS,
    persistFilters = false,
    storageKey = 'default',
    meta,
}: FilterableDataTableProps<TData>) => {
    const defaultData: FilterableDataTableData<TData> = {
        data: [],
        total: 0,
        totalFiltered: 0,
        limit: defaultRowsPerPage,
        offset: 0,
    };
    const tableData = data || defaultData;

    const { pagination, setPagination } = useUrlPagination({
        defaultPageSize: defaultRowsPerPage,
    });

    const { columnFilters, setColumnFilters, sorting, setSorting } = useFilteredDataTableState({
        storageKey,
        defaultFilters: initialFilters,
        defaultSorting: [],
        enabled: persistFilters,
    });

    // Map filters to their corresponding columns
    const filtersWithColumnMapping = filters.map((filter) => ({
        ...filter,
        columnId: filter.columnId || filter.id,
    }));

    // Set up the React Table instance
    const table = useReactTable<TData>({
        data: tableData.data || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        initialState: {
            columnFilters: initialFilters,
        },
        state: {
            sorting,
            columnFilters,
            pagination,
        },
        meta,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        pageCount: Math.ceil(tableData.totalFiltered / tableData.limit) || 0,
        rowCount: tableData.totalFiltered || 0,
    });

    // Reset to first page if current page exceeds available pages
    useEffect(() => {
        const pageCount = Math.ceil(tableData.totalFiltered / tableData.limit) || 0;

        if (pageCount > 0 && pagination.pageIndex >= pageCount) {
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }
    }, [tableData.totalFiltered, tableData.limit, pagination.pageIndex, setPagination]);

    // Notify parent components of state changes
    useEffect(() => {
        onPaginationChange?.(pagination);
    }, [pagination, onPaginationChange]);

    useEffect(() => {
        onSortingChange?.(sorting);
    }, [sorting, onSortingChange]);

    useEffect(() => {
        onFiltersChange?.(columnFilters);
    }, [columnFilters, onFiltersChange]);

    return (
        <Box sx={{ position: 'relative' }}>
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        borderRadius: 1,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <Box sx={{ opacity: isLoading ? 0.5 : 1 }}>
                <ActiveFilterChips filters={filters} columnFilters={columnFilters} onFiltersChange={setColumnFilters} />
                <TableBase
                    table={table}
                    noDataMessage={noDataMessage}
                    filters={filtersWithColumnMapping}
                    columnFilters={columnFilters}
                    onFiltersChange={setColumnFilters}
                    onRowClick={onRowClick}
                />
                <TablePagination table={table} totalRows={tableData.totalFiltered || 0} rowsPerPageOptions={rowsPerPageOptions} />
            </Box>
        </Box>
    );
};

export default FilterableDataTable;
